import type { Router } from '@angular/router';
import type { SideMenuCategory } from '@indutrust/shared-model';
import type { TranslocoService } from '@jsverse/transloco';
import type { MenuItem } from 'primeng/api';
import type { TopbarHelpMenuItemConfig } from './topbar-help-items.token';

/**
 * Maps brand help-menu configs to PrimeNG {@link MenuItem}s with resolved labels.
 *
 * @param factory - Brand-provided help item factory.
 * @param transloco - Transloco service for `global` scope labels.
 * @param router - Router used for internal navigation commands.
 */
export function resolveHelpMenuItems(
  factory: () => TopbarHelpMenuItemConfig[],
  transloco: TranslocoService,
  router: Router
): MenuItem[] {
  return factory().map(({ labelKey, routerLink, icon, target, url }) => {
    const item: MenuItem = {
      icon,
      label: transloco.translate(labelKey, {}, 'global'),
      target,
      url,
    };
    if (routerLink) {
      item.command = () => {
        router.navigate([routerLink]).catch(() => undefined);
      };
    }
    return item;
  });
}

/**
 * Normalizes a route path for comparison (leading slash, no trailing slash).
 */
export function normalizeRoutePath(path: string): string {
  const normalized =
    path.split('?')[0]?.split('#')[0]?.replace(/\/$/, '') ?? '';

  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

/**
 * Returns true when any side-menu item targets the given path.
 */
export function sideMenuHasRoute(
  categories: SideMenuCategory[] | undefined,
  path: string
): boolean {
  const normalizedPath = normalizeRoutePath(path);

  return (
    categories?.some((category) =>
      category.items.some(
        (item) => normalizeRoutePath(item.route.join('/')) === normalizedPath
      )
    ) ?? false
  );
}

/**
 * Filters help configs whose internal routes already appear in the side menu.
 * External links (`url` without `routerLink`) are always kept.
 */
export function filterHelpConfigsForSideMenu(
  configs: TopbarHelpMenuItemConfig[],
  categories: SideMenuCategory[] | undefined
): TopbarHelpMenuItemConfig[] {
  return configs.filter((config) => {
    if (!config.routerLink) {
      return true;
    }

    return !sideMenuHasRoute(categories, config.routerLink);
  });
}
